import React, { FormEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import {
  getAuthMode,
  isAuth0Enabled,
  isLocalAuthEnabled,
} from "../../utils/authMode";
import { completeLogin, redirectAfterLogin } from "./completeLogin";

const backendUrl = process.env.REACT_APP_baseUrl;

const Login = () => {
  const authMode = getAuthMode();
  const showLocal = isLocalAuthEnabled(authMode);
  const showAuth0 = isAuth0Enabled(authMode);
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Preserve the previous Auth0-only UX when local auth is not enabled.
    if (!showLocal && showAuth0) {
      window.location.href = `${backendUrl}/connect/auth0`;
    }
  }, [showLocal, showAuth0]);

  const startAuth0 = () => {
    window.location.href = `${backendUrl}/connect/auth0`;
  };

  const handleLocalLogin = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const loginRes = await axios.post(`${backendUrl}/auth/local`, {
        identifier,
        password,
      });

      const me = await completeLogin({
        jwt: loginRes.data.jwt,
        dispatch,
      });

      redirectAfterLogin(history, me);
    } catch (err: any) {
      console.error(err);
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message?.[0]?.messages?.[0]?.message ||
        intl.formatMessage({
          id: "login.invalid-credentials",
          defaultMessage: "Invalid email or password.",
        });
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!showLocal && showAuth0) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.200",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
        px: 2,
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            <FormattedMessage id="login.title" defaultMessage="Sign in" />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            <FormattedMessage
              id="login.subtitle"
              defaultMessage="Sign in to Gotologic Console"
            />
          </Typography>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {showLocal ? (
            <Box component="form" onSubmit={handleLocalLogin}>
              <TextField
                fullWidth
                margin="normal"
                label={intl.formatMessage({
                  id: "general.Email",
                  defaultMessage: "Email",
                })}
                type="email"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
              <TextField
                fullWidth
                margin="normal"
                label={intl.formatMessage({
                  id: "login.Password",
                  defaultMessage: "Password",
                })}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  <FormattedMessage
                    id="login.Sign-in"
                    defaultMessage="Sign in"
                  />
                )}
              </Button>
            </Box>
          ) : null}

          {showLocal && showAuth0 ? (
            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">
                <FormattedMessage id="login.or" defaultMessage="or" />
              </Typography>
            </Divider>
          ) : null}

          {showAuth0 ? (
            <Button fullWidth variant="outlined" onClick={startAuth0}>
              <FormattedMessage
                id="login.Continue-with-Auth0"
                defaultMessage="Continue with Auth0"
              />
            </Button>
          ) : null}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
